import { DateTime } from 'luxon';
import { IndexedDBService } from '../../indexed-db/services/db_service';
import { IndexedDBSyncService } from '../../indexed-db/services/sync_db_service';
import { AuthService } from '../services/auth/auth-service';
import { isOnline } from './helpers';
const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;

type SyncRequiredType = {
    url: string,
    type: string,
};

type RequestType = RequestInit & {
    url?: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
}

// Register a sync event
export const syncHandler = async (url: any, type: string, request: any) => {
    const syncRequired = syncRequiredWhileOffline({ url, type } as SyncRequiredType);
    if (isOnline()) {
      // when system is online
      // Send data immediately if online
      return await fetch(url, request);
    } else if (syncRequired) {
      // when system is offline
      // Save request to IndexedDB
      await saveRequestToIndexedDB(url, request);
    }
}

export const isUpdatePlanUrl = ({ url = '', type = ''} : SyncRequiredType) => {
  // eg. PUT call:  https://api-cdt.maersk.com/starconnect-voyage/voy-vvc-plansapi/v2/plans/2305?imo=9231236 
  // eg. PUT call:  https://api-cdt.maersk.com/starconnect-voyage/voy-vvc-plansapi/v2/plans/5709?sendPlanToShore=true&imo=9260445
  // draft plan POST create: eg. https://api-cdt.maersk.com/starconnect-voyage/voy-vvc-plansapi/v2/plans?imo=9260421

  const planPattern = /\/plans\/\d+\?.*imo=\d{7}/;
  const draftPlanPattern = /\/plans\?.*imo=\d{7}/;
  const draftPlanUpdatePattern = /\/plans\/-1\?.*imo=\d{7}/;
  const isUpdatePlan = planPattern.test(url) && type === 'PUT';
  const isDraftUpdatePlan = draftPlanUpdatePattern.test(url) && type === 'PUT';
  const isDraftPlan = draftPlanPattern.test(url) && type === 'POST';
  return (isUpdatePlan || isDraftPlan || isDraftUpdatePlan) ? true : false;
}

/** Matches cloud plans API paths for v1 and v2 (slim create/update). */
const isVoyVvcPlansApiUrl = (url: string = '') =>
  /voy-vvc-plansapi\/v[12]\/plans/.test(url);

export const syncRequiredWhileOffline = ({ url = '', type = ''} : SyncRequiredType) => {
  /* urls for background sync */
  if (!isOnline()) {
    // if offline — include v1 (PUT update) and v2 (POST slim create)
    if (isVoyVvcPlansApiUrl(url)) {
      // isUpdatePlanUrl gates by method+path: PUT /plans/{id}, POST /plans?, PUT /plans/-1
      const isUpdatePlanUrlApiCall = isUpdatePlanUrl({ url, type});
      return isUpdatePlanUrlApiCall;
    }
    // for vessel notifications : put api : BPR reject & Route notifications which are expected to be sent when vessel is offline and should be synced when vessel is online
    return (url.includes('notificationsapi/api/v1/VesselNotifications/') && type === 'POST');
  } else {
    return false;
  }
}

async function saveRequestToIndexedDB(url: any, request: any) {
  console.info("sync-url :", url);
  // Deduplicate offline PUT updates only (keep latest PUT per plan URL).
  // Create is POST /plans and is never deduped here — request.method === 'PUT' excludes create.
  // Updates use /v2/plans/{id} on cloud (legacy /v1 still matched for safety).
  if (request.method === 'PUT' && isVoyVvcPlansApiUrl(url)) {
    const existingRequests = await IndexedDBSyncService.request.getAll();
    const stripFlag = (u: string) => u.replace(/[?&]sendPlanToShore=(true|false)/g, '').replace(/\?&/, '?');
    for (const existing of existingRequests || []) {
      if (existing.request?.method === 'PUT' && stripFlag(existing.id) === stripFlag(url)) {
        await IndexedDBSyncService.request.deleteRequest(existing.id);
      }
    }
  }
  const savedRequest = {id: url, request};
  await IndexedDBSyncService.request.add(savedRequest);
}

export async function convertToUTCOffline (local: string, locationId: string) {
  // Convert local time to UTC
  const terminalOfflineVar = await IndexedDBService.terminals.get(locationId);
  const timezone = terminalOfflineVar?.olsonTimezone;
  const dt = DateTime.fromISO(local, { zone: timezone });
  return dt.toUTC().toISO();
}

export async function convertToLocalOffline (utc: number|string, locationId:string) {
  // Convert UTC to local time
  const utcString = (typeof(utc) === 'number' ) ? new Date(utc).toISOString()?.replace(/\.\d{3}/, '') : utc;
  const terminalOfflineVar = await IndexedDBService.terminals.get(locationId);
  const timezone = terminalOfflineVar?.olsonTimezone;
  const localTime = DateTime.fromISO(utcString, { zone: 'utc' }).setZone(timezone).toISO();
  return localTime;
}

export function fixDateTimeFormatOffline(dateTime: string) {
  if (isVesselOnCloud && !isOnline()) {
  // Convert dateTime to ISO format 2011-10-05T14:48:00.000Z to 2011-10-05T14:48:00Z
    return dateTime?.replace(/\.\d{3}/, '');
  } 
  return dateTime;
  
}

let _bgSyncInProgress = false;

export async function offlineBackgroundSyncManual() {
  // Guard against concurrent calls — the 'online' event and slow-network
  // recovery can both trigger this at the same time, causing duplicate API calls
  if (_bgSyncInProgress) {
    console.info('Background sync already in progress, skipping');
    return;
  }
  _bgSyncInProgress = true;

  try {
    //check any request queued for background sync
    const requestQueuedForSync = await IndexedDBSyncService.request.getAll();
    if (!requestQueuedForSync || requestQueuedForSync.length === 0) {
      console.info('No requests queued for background sync');
      return;
    }
    // fetch latest access token
    const scope = (<any>window)["appenv"].scope;
    const accessToken = await AuthService.getAccessToken(scope);
    if(accessToken){
      if (requestQueuedForSync && requestQueuedForSync.length > 0) {
        await doBackGroundSync(requestQueuedForSync, accessToken);
      }
      console.info('Manual sync completed');
      sessionStorage.setItem('bg-sync-manual-completed', new Date().getTime().toString());
    } else {
      console.error('Failed to fetch access token for manual sync');
      return;
    };
  } finally {
    _bgSyncInProgress = false;
  }
}

async function doBackGroundSync(requests: any[], accessToken: string) {
  const urlsToRemove: string[] = [];
  const ALL_BACKGROUND_SYNC_COMPLETED = 'all-bg-sync-data-completed';
  // for plan submission BG sync, we want to fetch the latest plans from server once all API calls are resent successfully, 
  // so dispatching a custom event with scheduleIds in detail which will be used to fetch the latest plans from server in the event listener in BackGroundOptimizationModelImpl
  const scheduleIds = await getScheduleIdsFromRequest(requests);
  // for BPR reject notification, we also want to capture the notifications info to show in the UI after manual sync is completed, 
  // so fetching the notifications info for the queued notification API calls and sending it in the custom event detail as well
  const notificationsInfo = await getNotificationsInfo(requests);
  // resend all queued plan submission API calls for background sync
  await manageAllDraftPlans(requests, urlsToRemove, accessToken);
  await manageAllApiCalls(requests, urlsToRemove, accessToken);

  console.info("All API calls queued for BG sync resent successfully");
  window.dispatchEvent(new CustomEvent(ALL_BACKGROUND_SYNC_COMPLETED,{ detail: {
    type: ALL_BACKGROUND_SYNC_COMPLETED,
    url: 'bg-sync',
    success: true,
    scheduleIds,
    notificationsInfo,
    syncTime: new Date().toISOString(),
  }}));
}

async function getScheduleIdsFromRequest(requests: any[]){
  const scheduleIds: string[] = [];
  // Filter to only plan API requests before processing
  const planRequests = requests.filter(
    (storedReq) => isVoyVvcPlansApiUrl(storedReq.id)
  );
  if (planRequests.length === 0) return scheduleIds;
  await Promise.all(
    planRequests.map(async (storedReq) => {
      try {
        const req = new Request(storedReq.id, {
          method: storedReq.request.method,
          headers: storedReq.request.headers,
          body: storedReq.request.body,
          cache: storedReq.request.cache,
          mode: storedReq.request.mode,
          url: storedReq.request.url,
        } as RequestType);
        const cloned = req.clone();
        const body = await cloned.json();
        if (body?.scheduleId) {
          scheduleIds.push(body.scheduleId);
        }
      } catch (error) {
        console.error('Failed to process stored request', error);
        return null;
      }
    })
  );
  return scheduleIds;
}

async function getNotificationsInfo(requests: any[]) {
  // Key by scheduleId (from the stringified data payload) instead of imoNo,
  // so multiple notification activities across different schedules for the same vessel are kept separate.
  const notifications: Record<string, { data: any; type: string; imoNo?: string; storeId?: string }[]> = {};
  // Filter to only notification API POST requests before processing
  const notificationRequests = requests.filter(
    (storedReq) => storedReq.id?.includes('notificationsapi/api/v1/VesselNotifications/') && storedReq.request?.method === 'POST'
  );
  if (notificationRequests.length === 0) return notifications;
  await Promise.all(
    notificationRequests.map(async (storedReq) => {
      try {
        const req = new Request(storedReq.id, {
          method: storedReq.request.method,
          headers: storedReq.request.headers,
          body: storedReq.request.body,
          cache: storedReq.request.cache,
          mode: storedReq.request.mode,
          url: storedReq.request.url,
        } as RequestType);
        const cloned = req.clone();
        const body = await cloned.json();
        // Parse the nested data JSON to extract scheduleId
        const parsedData = typeof body?.data === 'string' ? JSON.parse(body.data) : body?.data;
        const scheduleId = parsedData?.scheduleId?.toString() || '';
        if (scheduleId) {
          if (!notifications[scheduleId]) {
            notifications[scheduleId] = [];
          }
          notifications[scheduleId].push({ data: body?.data, type: body?.type, imoNo: body?.imoNo?.toString(), storeId: storedReq.id });
        }
      } catch (error) {
        console.error('Failed to process stored notification request', error);
        return null;
      }
    })
  );
  return notifications;
}

async function manageAllDraftPlans(requests: any[], urlsToRemove: string[] = [], accessToken: string = '') {
  // Filter to only plan API requests before processing
  const planRequests = requests.filter(
    (storedReq) => isVoyVvcPlansApiUrl(storedReq.id)
  );
  if (planRequests.length === 0) return;
  for (const req of planRequests) {
    try {
        const planPattern = /\/plans\/\d+\?.*imo=\d{7}/;
        const draftPlanPattern = /\/plans\?.*imo=\d{7}/;
        const url = req?.id;
        const type = req?.request?.method;
        const isDraftPlan = draftPlanPattern.test(url) && type === 'POST';
        const requestUrl = new URL(req.id) + (req.id.includes('?') ? '&' : '?') + 'backgroundSync=true';  
        if (isDraftPlan) {
          // Clone headers
          const newHeaders = new Headers(req.request.headers);
          if (accessToken){
            // Update the Authorization header with the latest access token
            newHeaders.set('Authorization', `Bearer ${accessToken}`);
          }
          // Create a new request
          const modifiedRequest = new Request(requestUrl, {
            headers: newHeaders,
            method: req.request.method,
            body: req.request.body,
            mode: req.request.mode,
            credentials: req.request.credentials,
            cache: req.request.cache,
            redirect: req.request.redirect,
            referrer: req.request.referrer,
            integrity: req.request.integrity,
          });
          let response = await fetch(modifiedRequest);
          const planData = await response.json();
          if (response.ok) {
            console.info("Drafts Plans post call resent request successfully:", req);
            urlsToRemove.push(req.id);
            //await removeRequestFromIndexedDB(req.id, 'SyncDatabase', 'bg-sync-api-store');
            await IndexedDBSyncService.request.deleteRequest(req.id);
            const clonedRequests = requests;
              // using the scheduleId to filter out the request
              // that was used to create the plan
              // and then clone the request to modify the body
              // and resend it with the new id received from the previous post response
              //var selectedRequest: Request | undefined = undefined;
              const selectedRequests = await Promise.all(
                clonedRequests.map(async (storedReq) => {
                  try {
                    const reqObj = new Request(storedReq.id, {
                      method: storedReq.request.method,
                      headers: storedReq.request.headers,
                      body: storedReq.request.body, // must be string or FormData etc.
                      cache:storedReq.request.cache,
                      mode: storedReq.request.mode,
                      url: storedReq.request.url,
                    } as RequestType);
                    const cloned = reqObj.clone();
                    const body = await cloned.json();
                    if ((body?.scheduleId === planData?.scheduleId) && (body?.id === -1) && (reqObj?.method === 'PUT')) {
                      return reqObj;
                    }
                    return null;
                  } catch (error) {
                    console.error('Failed to process stored request', error);
                    return null;
                  }
                }) 
              );
              const selectedRequest = selectedRequests.find((req) => req !== null && req instanceof Request
                ) as Request | undefined; // Filter out null values and ensure it's a Request object
              if (selectedRequest) {
                console.info('selectedRequest:', selectedRequest);
                const cloned = selectedRequest.clone();
                const originalBody = await cloned.json();
                if(originalBody) {
                  // Modify the body
                  originalBody.id = planData?.id;
                  const updatedUrl = replacePlanIdInUrl(selectedRequest?.url, planData?.id);
                  // add backgroundSync param to request url
                  const requestUrl = new URL(updatedUrl) + (updatedUrl.includes('?') ? '&' : '?') + 'backgroundSync=true';
                  console.info("requestUrl >>>",requestUrl);
                  // Clone headers
                  const newHeaders = new Headers(req.request.headers);
                  if (accessToken){
                    // Update the Authorization header with the latest access token
                    newHeaders.set('Authorization', `Bearer ${accessToken}`);
                  }
                  // Recreate the request with modified body
                  const modifiedRequest = new Request(requestUrl, {
                    method: selectedRequest.method,
                    headers: newHeaders,
                    body: JSON.stringify(originalBody),
                    mode: req.request.mode,
                    credentials: req.request.credentials,
                    cache: req.request.cache,
                    redirect: req.request.redirect,
                    referrer: req.request.referrer,
                    referrerPolicy: req.request.referrerPolicy,
                    integrity: req.request.integrity,
                  });
                  console.info('originalBody:', originalBody);
                  console.info('modifiedRequest:', modifiedRequest);
                  response = await fetch(modifiedRequest);
                  if (response.ok) {
                    const syncTime = new Date().toISOString();
                    window.dispatchEvent(new CustomEvent('manual-sync-post-data-completed', 
                      {
                        detail: {
                          type:'manual-sync-post-data-completed',
                          url: req.id,
                          success: true,
                          syncTime
                        }
                      }
                    ));
                    urlsToRemove.push(selectedRequest.url);
                    await IndexedDBSyncService.request.deleteRequest(selectedRequest.url);
                    console.info("Plans put call resent request successfully:", selectedRequest);
                  }
                } else {
                  const syncTime = new Date().toISOString();
                  window.dispatchEvent(new CustomEvent('manual-sync-post-data-completed', 
                    {
                      detail: {
                        type:'manual-sync-post-data-completed',
                        url: req.id,
                        success: true,
                        syncTime
                      }
                    }
                  ));
                  }
              }
          }
        }
    } catch (error) {
      console.info("Failed to resend plans put or post request:", req, error);
    }
  }
}

async function manageAllApiCalls(requests: any[], urlsToRemove: string[] = [], accessToken: string = '') {
  // Filter out requests already handled by manageAllDraftPlans and notification requests
  // (notification requests are handled separately by processNotificationsAfterSync in BackGroundOptimizationModelImpl)
  const remainingRequests = requests.filter(
    (storedReq) => !urlsToRemove.includes(storedReq.id) && !storedReq.id?.includes('notificationsapi/api/v1/VesselNotifications/')
  );
  if (remainingRequests.length === 0) return;
  for (const req of remainingRequests) {
    try {
        // add backgroundSync param to request url
        const requestUrl = new URL(req.id) + (req.id.includes('?') ? '&' : '?') + 'backgroundSync=true';
        const newHeaders = new Headers(req.request.headers);
        if (accessToken){
          // Update the Authorization header with the latest access token
          newHeaders.set('Authorization', `Bearer ${accessToken}`);
        }
        // Recreate the request with modified body
        const modifiedRequest = new Request(requestUrl, {
          method: req.request.method,
          headers: newHeaders,
          body: req.request.body,
          mode: req.request.mode,
          credentials: req.request.credentials,
          cache: req.request.cache,
          redirect: req.request.redirect,
          referrer: req.request.referrer,
          referrerPolicy: req.request.referrerPolicy,
          integrity: req.request.integrity,
        });
        const response = await fetch(modifiedRequest);
        if (response.ok) {
          const syncTime = new Date().toISOString();
          window.dispatchEvent(new CustomEvent('manual-sync-post-data-completed', 
            {
              detail: {
                type:'manual-sync-post-data-completed',
                url: req.id,
                success: true,
                syncTime
              }
            }
          ));
          urlsToRemove.push(req.id);
          await IndexedDBSyncService.request.deleteRequest(req.id);
          console.info("Resent request successfully:", req);
        } else if (response.status === 503) {
          // Network unavailable (e.g., SW returned 503) — skip, will retry on next sync
          console.warn("Network unavailable (503), will retry on next sync:", req.id);
        } else {
          // Non-retryable server error — log and remove to prevent infinite retry
          console.error(`Request failed with status ${response.status}, removing:`, req.id);
          urlsToRemove.push(req.id);
          await IndexedDBSyncService.request.deleteRequest(req.id);
        }
    } catch (error) {
      console.info("Failed to resend request:", req, error);
      window.dispatchEvent(new CustomEvent('manual-sync-post-data-completed', 
        {
          detail: {
            type:'manual-sync-post-data-completed',
            url: req.id,
            success: false,
            error: error.message
          }
        }
      ));
    }
  }
}

function replacePlanIdInUrl(url:string, newId:string) {
  return url.replace(/(\/plans\/)-1(?=\?)/, `$1${newId}`);
} 

export async function clearSiteData() {
    if(isVesselOnCloud && isOnline())
    {
      // 1. Unregister all service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          console.info('[Clear] Unregistering SW:', registration);
          await registration.unregister();
        }
      }
      // 2. Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        for (const cacheName of cacheNames) {
          console.info('[Clear] Deleting cache:', cacheName);
          await caches.delete(cacheName);
        }
      }
      // 2. Clear IndexedDB
      indexedDB.deleteDatabase('SyncDatabase');
      indexedDB.deleteDatabase('VesselDB');    
      indexedDB.deleteDatabase('MapsDB');
      
      // 4. Clear localStorage and sessionStorage
      console.info('[Clear] Clearing localStorage and sessionStorage');
      localStorage.clear();
      sessionStorage.clear();

      alert('Site data cleared. Reloading...');
      setTimeout(() => {
          window.location.reload(); //force reload so new data can be cached
      }, 100);
    }
}

